/**
 * Suggestions pour un paiement que le rapprochement automatique n'a pas su
 * placer.
 *
 * Ce module encode la démarche manuelle réellement suivie par l'administration,
 * telle qu'elle a été décrite :
 *
 * > « Quand j'ai une communication avec le nom des enfants, je recherche
 * > l'enfant, qui me donne le parent, qui me donne les factures ouvertes, et je
 * > match par montant. Si pas de communication, je regarde le compte ou le nom
 * > de l'émetteur et je recherche dans la base l'utilisateur, ou alors dans la
 * > compta d'autres versements arrivés de ce numéro de compte : si d'autres
 * > paiements sont arrivés de ce compte, je peux trouver le nom, puis la facture
 * > ouverte. »
 *
 * Rien n'est appliqué. Le module **classe des candidats** et dit sur quoi il
 * s'appuie ; un humain choisit. C'est la seule posture tenable : ces paiements
 * sont précisément ceux dont la communication ne prouve rien.
 *
 * ## Pourquoi le nom de l'émetteur ne suffit jamais
 *
 * Les enfants ont entre 2 ans et demi et 13 ans : ils ne font pas de virements.
 * Le titulaire du compte n'est donc jamais l'enfant, et souvent pas non plus le
 * parent inscrit. Un cas réel des relevés : la communication dit « Regular
 * Hoessels Charlotte et Sacha » et le compte appartient à « Laska Rovena ».
 *
 * D'où deux pistes distinctes, et non une seule :
 *
 * - le **nom dans la communication**, qui est celui des enfants ;
 * - le **titulaire du compte**, qu'on cherche parmi les parents **et parmi les
 *   personnes autorisées** — une grand-mère ou un beau-parent qui paie y figure.
 */

/** Sur quoi une suggestion s'appuie. Plusieurs pistes peuvent converger. */
export type SuggestionSource =
  /** L'IBAN a déjà été rattaché à cette famille par un humain. */
  | 'iban_confirmed'
  /** D'autres paiements de ce compte ont déjà été affectés à cette famille. */
  | 'iban_history'
  /** Le nom d'un enfant de cette famille apparaît dans la communication. */
  | 'kid_name'
  /** Le titulaire du compte est une personne autorisée de cette famille. */
  | 'authorized_person'
  /** Le titulaire du compte porte le nom du parent inscrit. */
  | 'account_holder'
  /**
   * Une facture a été trouvée par son numéro, dans une recherche manuelle.
   *
   * Cette piste n'est jamais produite par `suggestFamilies` : elle vient de
   * l'écran, quand l'administration tape elle-même un numéro. Elle est tenue
   * pour solide parce que c'est un humain qui a lu le numéro.
   */
  | 'invoice_number'
  /** Une famille a été trouvée par son nom, dans une recherche manuelle. */
  | 'family_search';

export interface OpenInvoice {
  invoiceId: string;
  invoiceNumber: string;
  outstandingCents: number;
  dueDate: string | null;
  /**
   * Lien vers le PDF de la facture, quand il existe.
   *
   * 3 656 factures sur 3 689 en ont un. Les 33 restantes s'affichent sans
   * lien : mieux vaut un numéro non cliquable qu'un lien mort.
   */
  pdfUrl?: string | null;
}

export interface FamilyHit {
  userId: string;
  label: string;
  source: SuggestionSource;
  /** Ce qui a déclenché : un nom d'enfant, un IBAN, un nom de titulaire. */
  matched?: string;
}

export interface Suggestion {
  userId: string;
  label: string;
  sources: SuggestionSource[];
  score: number;
  /** Formulé en français, destiné à être lu tel quel dans l'écran. */
  reasons: string[];
  openInvoices: OpenInvoice[];
  /** Une facture ouverte dont le solde égale exactement le paiement. */
  exactInvoice: OpenInvoice | null;
  /** Le total des factures ouvertes égale exactement le paiement. */
  exactOnTotal: boolean;
  /**
   * Répartition proposée, facture la plus ancienne d'abord.
   *
   * Le cas réel qui l'a rendue nécessaire : un virement de 459,00 € d'une
   * famille identifiée avec certitude par le nom de ses deux enfants, face à
   * cinq factures ouvertes totalisant 514,00 €. Ni une facture exacte, ni le
   * total — un paiement partiel, et aucun sous-ensemble ne tombe juste.
   *
   * L'imputation par ancienneté est la pratique comptable usuelle et donne un
   * point de départ que l'humain ajuste. Vide quand le paiement dépasse le
   * total dû, cas où il faut d'abord décider du surplus.
   */
  proposedSplit: { invoice: OpenInvoice; amountCents: number }[];
}

/**
 * Poids de chaque piste.
 *
 * L'ordre reproduit la confiance de la démarche manuelle : un IBAN qu'un humain
 * a déjà rattaché vaut mieux qu'un nom qui se ressemble. Les valeurs ne sont pas
 * une science ; elles servent à ordonner une liste que l'humain parcourt de
 * haut en bas.
 */
const POIDS: Record<SuggestionSource, number> = {
  iban_confirmed: 60,
  iban_history: 45,
  kid_name: 40,
  authorized_person: 30,
  account_holder: 25,
  invoice_number: 55,
  family_search: 20,
};

const LIBELLES: Record<SuggestionSource, string> = {
  iban_confirmed: 'compte déjà rattaché à cette famille',
  iban_history: "d'autres paiements de ce compte ont été affectés à cette famille",
  kid_name: 'nom d\'un enfant de la famille dans la communication',
  authorized_person: 'titulaire du compte inscrit comme personne autorisée',
  account_holder: 'titulaire du compte au nom du parent',
  invoice_number: 'facture trouvée par son numéro',
  family_search: 'famille trouvée par son nom',
};

/**
 * Un montant qui tombe juste est le signal décisif de la démarche manuelle
 * (« je match par montant »). Il pèse donc autant que la meilleure piste
 * d'identification, sans jamais suffire seul : une famille n'est proposée que
 * si une piste l'a d'abord désignée.
 */
const BONUS_MONTANT_EXACT = 35;
/**
 * Le total des factures ouvertes qui tombe juste : c'est le cas des deux
 * paniers réglés d'un seul virement, dont on sait qu'il ne peut pas être évité.
 */
const BONUS_TOTAL_EXACT = 25;

/**
 * Découpe un nom en jetons comparables : sans accents, en majuscules, sans
 * ponctuation.
 *
 * Les noms arrivent sous toutes les formes — `MCGOURTY-GREEN`,
 * `Mme Marcela BARBOSA DE SOU` tronqué par la banque, `Stroiu Matteo/Stroiu
 * Leo`. Comparer des chaînes entières ne donnerait rien ; comparer des jetons
 * marche.
 *
 * Les jetons d'une ou deux lettres sont écartés : `M`, `Mme`, `DE`, `LA`
 * apparaissent partout et ne distinguent personne.
 */
export function nameTokens(value: string | null | undefined): Set<string> {
  if (!value) return new Set();
  const sans = value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase();
  const jetons = sans.split(/[^A-Z0-9]+/).filter((t) => t.length >= 3);
  // Les civilités et particules ne distinguent personne.
  const BRUIT = new Set(['MME', 'MLLE', 'MONSIEUR', 'MADAME', 'THE', 'DES', 'DEL', 'VAN', 'DER', 'DEN']);
  return new Set(jetons.filter((t) => !BRUIT.has(t)));
}

/**
 * Un nom de personne est-il présent dans un texte ?
 *
 * Exige que **tous** les jetons distinctifs du nom soient présents. Un seul
 * jeton commun — un prénom répandu, un nom de famille fréquent — ne suffit pas :
 * sur quelques milliers d'enfants, `Charlotte` seul désignerait dix familles.
 */
export function nameAppearsIn(personName: string, texte: string): boolean {
  const attendus = nameTokens(personName);
  if (attendus.size === 0) return false;
  const presents = nameTokens(texte);
  for (const t of attendus) if (!presents.has(t)) return false;
  return true;
}

export interface SuggestInput {
  amountCents: number;
  communication: string | null;
  counterpartyIban: string | null;
  counterpartyName: string | null;
}

/**
 * Tout ce dont les suggestions ont besoin de la base.
 *
 * Chaque méthode correspond à une des pistes de la démarche manuelle, ce qui
 * rend le module testable sans base et lisible sans connaître le schéma.
 */
export interface SuggestGateway {
  /** L'IBAN a-t-il déjà été rattaché à une famille, par un humain ? */
  familyByConfirmedIban(iban: string): Promise<FamilyHit | null>;
  /** Quelles familles ont déjà reçu un paiement affecté depuis cet IBAN ? */
  familiesByIbanHistory(iban: string): Promise<FamilyHit[]>;
  /** Quels enfants portent un nom présent dans ce texte ? */
  familiesByKidName(communication: string): Promise<FamilyHit[]>;
  /** Quelles familles ont une personne autorisée de ce nom ? */
  familiesByAuthorizedPerson(name: string): Promise<FamilyHit[]>;
  /** Quels parents portent ce nom ? */
  familiesByAccountHolder(name: string): Promise<FamilyHit[]>;
  /** Les factures encore dues de ces familles. */
  openInvoicesFor(userIds: string[]): Promise<Map<string, OpenInvoice[]>>;
}

/**
 * Classe les familles candidates pour un paiement.
 *
 * Les pistes sont toutes explorées, et non arrêtées à la première qui répond :
 * leur convergence est en soi une information. Un IBAN connu **et** un nom
 * d'enfant dans la communication **et** un montant qui tombe juste, c'est une
 * quasi-certitude ; l'écran peut alors proposer un seul clic.
 */
export async function suggestFamilies(
  payment: SuggestInput,
  gateway: SuggestGateway,
  options: { limit?: number } = {},
): Promise<Suggestion[]> {
  const hits: FamilyHit[] = [];

  if (payment.counterpartyIban) {
    const confirme = await gateway.familyByConfirmedIban(payment.counterpartyIban);
    if (confirme) hits.push(confirme);
    hits.push(...(await gateway.familiesByIbanHistory(payment.counterpartyIban)));
  }
  if (payment.communication) {
    hits.push(...(await gateway.familiesByKidName(payment.communication)));
  }
  if (payment.counterpartyName) {
    hits.push(...(await gateway.familiesByAuthorizedPerson(payment.counterpartyName)));
    hits.push(...(await gateway.familiesByAccountHolder(payment.counterpartyName)));
  }

  if (hits.length === 0) return [];

  // Regrouper par famille : plusieurs pistes peuvent désigner la même.
  const parFamille = new Map<string, { label: string; hits: FamilyHit[] }>();
  for (const h of hits) {
    const e = parFamille.get(h.userId) ?? { label: h.label, hits: [] };
    e.hits.push(h);
    parFamille.set(h.userId, e);
  }

  const factures = await gateway.openInvoicesFor([...parFamille.keys()]);

  const suggestions: Suggestion[] = [];
  for (const [userId, { label, hits: siens }] of parFamille) {
    const ouvertes = (factures.get(userId) ?? []).filter((f) => f.outstandingCents > 0);
    const sources = [...new Set(siens.map((h) => h.source))];

    // Le score d'une famille est celui de sa meilleure piste, plus une part des
    // autres : deux pistes faibles qui convergent valent mieux qu'une seule,
    // sans pour autant dépasser une piste forte.
    const poids = sources.map((s) => POIDS[s]).sort((a, b) => b - a);
    let score = (poids[0] ?? 0) + poids.slice(1).reduce((s, p) => s + p / 3, 0);

    const exacte = ouvertes.find((f) => f.outstandingCents === payment.amountCents) ?? null;
    const total = ouvertes.reduce((s, f) => s + f.outstandingCents, 0);
    const totalExact = ouvertes.length > 1 && total === payment.amountCents;

    if (exacte) score += BONUS_MONTANT_EXACT;
    if (totalExact) score += BONUS_TOTAL_EXACT;

    // Répartition par ancienneté, tant qu'il reste du paiement à placer.
    const split: { invoice: OpenInvoice; amountCents: number }[] = [];
    if (ouvertes.length > 0 && total >= payment.amountCents) {
      const parAnciennete = [...ouvertes].sort((a, b) =>
        (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999'),
      );
      let reste = payment.amountCents;
      for (const f of parAnciennete) {
        if (reste <= 0) break;
        const part = Math.min(reste, f.outstandingCents);
        split.push({ invoice: f, amountCents: part });
        reste -= part;
      }
    }

    const reasons = siens.map((h) =>
      h.matched ? `${LIBELLES[h.source]} (${h.matched})` : LIBELLES[h.source],
    );
    if (exacte) {
      reasons.push(`le montant correspond exactement à la facture ${exacte.invoiceNumber}`);
    }
    if (totalExact) {
      reasons.push(`le montant correspond au total de ${ouvertes.length} factures ouvertes`);
    }
    if (ouvertes.length === 0) {
      reasons.push('aucune facture ouverte — vérifier avant d\'affecter');
    } else if (!exacte && !totalExact && split.length > 1) {
      reasons.push(
        `paiement partiel : réparti sur ${split.length} facture(s) de la plus ancienne à la plus récente`,
      );
    } else if (!exacte && !totalExact && total < payment.amountCents) {
      reasons.push('le paiement dépasse le total dû — décider du surplus');
    }

    suggestions.push({
      userId,
      label,
      sources,
      score: Math.round(score),
      reasons: [...new Set(reasons)],
      openInvoices: ouvertes,
      exactInvoice: exacte,
      exactOnTotal: totalExact,
      proposedSplit: split,
    });
  }

  suggestions.sort((a, b) => b.score - a.score || a.label.localeCompare(b.label));
  return suggestions.slice(0, options.limit ?? 8);
}

/**
 * Vrai quand une suggestion est assez forte pour qu'un seul clic suffise.
 *
 * Deux conditions cumulées : une piste d'identification solide, et un montant
 * qui tombe juste. Cela ne déclenche jamais une écriture automatique — cela
 * décide seulement si l'écran met en avant un bouton « affecter » ou s'il
 * demande de choisir.
 */
export function isConfident(s: Suggestion): boolean {
  const pisteSolide = s.sources.some(
    (src) =>
      src === 'iban_confirmed' ||
      src === 'iban_history' ||
      src === 'kid_name' ||
      src === 'invoice_number',
  );
  return pisteSolide && (s.exactInvoice !== null || s.exactOnTotal);
}
